import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { IndustryExpertise } from '../../../models/industryExpertise';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-industry-expertise-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './industry-expertise-list.component.html',
  styleUrl: './industry-expertise-list.component.scss'
})
export class IndustryExpertiseListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  industryExpertises = signal<IndustryExpertise[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadIndustryExpertises();
  }

  loadIndustryExpertises(): void {
    this.spinnerService.show();
    this.lutService.getIndustryExpertise().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortIndustryExpertises(data);
        this.industryExpertises.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading industry expertises:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortIndustryExpertises(industryExpertises: IndustryExpertise[]): IndustryExpertise[] {
    return [...industryExpertises].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateIndustryExpertise(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(industryExpertise: IndustryExpertise): void {
  }

  onEditIndustryExpertise(industryExpertise: IndustryExpertise): void {
  }

  onDeleteIndustryExpertise(industryExpertise: IndustryExpertise): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(industryExpertise.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalIndustryExpertises = this.industryExpertises();

          // 1. Optimistically remove from local state immediately
          this.industryExpertises.update(industryExpertises => industryExpertises.filter(ie => ie.id !== industryExpertise.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteIndustryExpertise(industryExpertise.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Industry expertise deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('industryExpertises', this.industryExpertises());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting industry expertise:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.industryExpertises.set(originalIndustryExpertises);
              this.toast.error(`<strong>Failed to delete Industry Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: IndustryExpertise, newName: string} | {item: IndustryExpertise, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: IndustryExpertise;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.industryExpertises.update(industryExpertises => {
      const updated = industryExpertises.map(ie => ie.id === event.item.id ? updatedItem : ie);
      return this.sortIndustryExpertises(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateIndustryExpertise(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Industry expertise updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('industryExpertises', this.industryExpertises());
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.industryExpertises.update(industryExpertises => {
          const updated = industryExpertises.map(ie => ie.id === event.item.id ? response : ie);
          return this.sortIndustryExpertises(updated);
        });
      },
      error: (error) => {
        console.error('Error updating industry expertise:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.industryExpertises.update(industryExpertises =>
          industryExpertises.map(ie => ie.id === event.item.id ? originalItem : ie)
        );
        this.toast.error(`<strong>Failed to update Industry Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newIndustryExpertise: IndustryExpertise = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.industryExpertises.update(industryExpertises => this.sortIndustryExpertises([...industryExpertises, newIndustryExpertise]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createIndustryExpertise({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Industry expertise created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('industryExpertises', this.industryExpertises());
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.industryExpertises.update(industryExpertises => {
          const updated = industryExpertises.map(ie => ie.id === tempId ? response : ie);
          return this.sortIndustryExpertises(updated);
        });
      },
      error: (error) => {
        console.error('Error creating industry expertise:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.industryExpertises.update(industryExpertises =>
          industryExpertises.filter(ie => ie.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Industry Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
